import React, { useEffect, useState } from 'react';
import { MAX_CP_NAME_LENGTH } from '@bynder-studio/misc';
import { Button, InputField, Modal, Thumbnail, token } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { IconSync } from '@bynder/icons';
import generateTestId from 'packages/helpers/testIdHelpers';
import { ModalPropsType } from '../../types';
import modalContainer from 'packages/common/modalContainer';
import { useDeferredClose } from 'packages/hooks/useDeferredClose';
import { Paragraph } from 'packages/common/Typography/Typography';

const CreateModal = ({ elementName, isOpen, onClose, onCreate, selectedElements }: ModalPropsType) => {
    const [shouldRender, onCloseAnimationEnd] = useDeferredClose(isOpen);
    const [propertyName, setPropertyName] = useState<string>(elementName.slice(0, MAX_CP_NAME_LENGTH));
    const unlockedWithoutContentProperty = selectedElements.filter((el) => !el.contentPropertyId && !el.locked);

    const selectedCount = unlockedWithoutContentProperty.length;
    const isSingleSelect = selectedElements.length === 1;
    const isInputValid = propertyName.length <= MAX_CP_NAME_LENGTH;
    const isActionDisabled = isSingleSelect && (!propertyName.length || propertyName.length > MAX_CP_NAME_LENGTH);

    const { translate } = useTranslate();

    const handleCreate = () => {
        onCreate(propertyName);
        onClose();
    };

    const onKeyEnter = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            if (isInputValid && !isActionDisabled) {
                handleCreate();
            }
        }
    };

    useEffect(() => {
        if (isOpen && elementName !== propertyName) {
            setPropertyName(elementName.slice(0, MAX_CP_NAME_LENGTH));
        }
    }, [isOpen, elementName]);

    if (!shouldRender) {
        return null;
    }

    return (
        <Modal
            container={modalContainer}
            isClosedOnOverlayClick
            title={
                selectedCount === 1
                    ? translate('editor.sidebar.shots.contentProperty.create.modal.title')
                    : translate('editor.sidebar.shots.contentProperty.create.modal.title.multiple', {
                          count: selectedCount,
                      })
            }
            isOpen={isOpen}
            onCloseAnimationEnd={onCloseAnimationEnd}
            onClose={onClose}
            actionPrimary={
                <Button
                    variant="primary"
                    onClick={handleCreate}
                    isDisabled={isActionDisabled || !isInputValid}
                    {...generateTestId('editor : modal : new content property create button')}
                >
                    {isSingleSelect
                        ? translate('editor.sidebar.shots.contentProperty.create.modal.create')
                        : translate('editor.sidebar.shots.contentProperty.create.modal.create.multiple')}
                </Button>
            }
            actionSecondary={
                <Button
                    variant="secondary"
                    onClick={onClose}
                    {...generateTestId('editor : modal : new content property cancel button')}
                >
                    {translate('editor.sidebar.shots.contentProperty.create.modal.cancel')}
                </Button>
            }
        >
            {isSingleSelect && selectedCount === 1 ? (
                <InputField
                    label={translate('editor.sidebar.shots.contentProperty.create.modal.label')}
                    value={propertyName}
                    placeholder={translate('editor.sidebar.shots.contentProperty.create.modal.placeholder')}
                    onChange={setPropertyName}
                    onKeyDown={onKeyEnter}
                    isInvalid={!isInputValid}
                    helperText={!isInputValid && translate('input.error.max.length')}
                    icon={
                        <Thumbnail
                            shape="circle"
                            size="xs"
                            backgroundColor={token.teal500}
                            icon={<IconSync size="16" style={{ color: token.white }} />}
                        />
                    }
                    hasClearButton
                    {...generateTestId('editor : modal : new content property name input')}
                />
            ) : (
                <Paragraph>
                    {translate('editor.sidebar.shots.contentProperty.create.modal.multiple.description')}
                </Paragraph>
            )}
        </Modal>
    );
};

export default React.memo(CreateModal);
